import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, ArrowLeft, Lock, Mail } from 'lucide-react';
import { APP_CONFIG } from '@/constants/branding';

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const from = location.state?.from?.pathname || '/system/dashboard';

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg('');

        const { error } = await login(email, password);

        if (error) {
            setErrorMsg('Credenciais inválidas. Verifique seu e-mail e senha.');
            setLoading(false);
        } else {
            navigate(from, { replace: true });
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0F2847 0%, #193D6D 40%, #2A5487 100%)' }}>

            <Helmet>
                <title>Acesso ao Sistema - {APP_CONFIG.APP_NAME} | Plataforma de Gestão Pericial</title>
                <meta name="description" content="Acesse o sistema PROVATIO para gerenciar seus processos periciais, laudos e honorários." />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            {/* Decorative elements */}
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full opacity-[0.04]"
                    style={{ background: 'radial-gradient(circle, #FCD34D, transparent)' }} />
                <div className="absolute -bottom-60 -right-60 w-[700px] h-[700px] rounded-full opacity-[0.03]"
                    style={{ background: 'radial-gradient(circle, #FCD34D, transparent)' }} />
                <div className="absolute inset-0 opacity-[0.02]"
                    style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
            </div>

            {/* Back link */}
            <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-white/60 hover:text-[#FCD34D] transition-all duration-300 text-sm font-medium z-10" id="login-back-link">
                <ArrowLeft className="w-4 h-4" />
                Voltar ao HUB
            </Link>

            {/* Login Card */}
            <div className="relative z-10 w-full max-w-md mx-4 bg-white/[0.07] backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl shadow-black/20 p-10">

                {/* Logo & Title */}
                <div className="text-center mb-10">
                    <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center shadow-inner">
                        <span className="text-3xl">🔬</span>
                    </div>
                    <h1 className="text-2xl font-bold text-white tracking-wider mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Acesso ao Sistema
                    </h1>
                    <p className="text-white/50 text-sm">Entre com suas credenciais para continuar</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-5" id="login-form">
                    {errorMsg && (
                        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm text-center backdrop-blur-sm" role="alert">
                            {errorMsg}
                        </div>
                    )}

                    <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-white/70 mb-2 tracking-wide">E-mail</label>
                        <div className="relative group">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 group-focus-within:text-[#FCD34D] transition-colors" />
                            <input
                                id="email"
                                type="email"
                                placeholder="admin@provatio.com.br"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/[0.05] border border-white/10 text-white placeholder:text-white/25 focus:outline-none focus:border-[#FCD34D]/40 focus:bg-white/[0.08] focus:ring-1 focus:ring-[#FCD34D]/20 transition-all duration-300 text-sm"
                                autoComplete="email"
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="password" className="block text-sm font-semibold text-white/70 mb-2 tracking-wide">Senha</label>
                        <div className="relative group">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 group-focus-within:text-[#FCD34D] transition-colors" />
                            <input
                                id="password"
                                type="password"
                                placeholder="••••••••"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/[0.05] border border-white/10 text-white placeholder:text-white/25 focus:outline-none focus:border-[#FCD34D]/40 focus:bg-white/[0.08] focus:ring-1 focus:ring-[#FCD34D]/20 transition-all duration-300 text-sm"
                                autoComplete="current-password"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="w-full py-4 rounded-full bg-[#FCD34D] text-[#193D6D] font-bold text-sm tracking-wider uppercase transition-all duration-300 hover:bg-[#FBBF24] hover:shadow-xl hover:shadow-[#FCD34D]/20 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none mt-3"
                        disabled={loading}
                        id="login-submit-btn"
                    >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Entrar no Sistema'}
                    </button>
                </form>

                <div className="text-center mt-8">
                    <Link to="/system/signup" className="text-white/40 hover:text-[#FCD34D] transition-colors text-sm" id="login-signup-link">
                        Não tem uma conta? <span className="font-semibold underline underline-offset-4">Cadastre-se</span>
                    </Link>
                </div>

                {/* Bottom gold line */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/3 h-[2px] bg-gradient-to-r from-transparent via-[#FCD34D]/40 to-transparent rounded-full"></div>
            </div>
        </div>
    );
};

export default LoginPage;